import { resourceAPIURL } from "../config";
import useAuth from "./useAuth";

// const app = initializeApp({ apiKey: firebaseAPIKey });
// const auth = getAuth(app);

export default function useApiHelper(
  config = { apiUrl: undefined, headers: {}, options: {} },
) {
  const { user, loading } = useAuth();
  const baseUrl = config.apiUrl || `${resourceAPIURL}/v1`;

  const _getBearer = async () => {
    try {
      const id = await user?.getIdTokenResult();
      // console.log(id);
      if (id) {
        // console.log("bearer with token")
        return { Authorization: `Bearer ${id.token}` };
      } else {
        // console.log("bearer without token")
        return { Authorization: `Bearer` };
      }
    } catch (err) {
      console.error(err);
      return { Authorization: `Bearer` };
    }
  };

  const _getReqOpts = async () => {
    const reqOpts = {
      headers: {
        "Content-Type": "application/json",
        ...(await _getBearer()),
      },
      ...config.options,
    };

    return reqOpts;
  };

  async function _handleResponse<T>(res: Response): Promise<T> {
    if (!res.ok) {
      let message = `HTTP Error: ${res.status}`;

      if (res.status == 400) {
        try {
          let json = await res.json();
          if (json.message) {
            message = json.message;
          }
        } catch {}
      }

      throw new Error(message);
    }

    return await res.json();
  }

  async function get<T>(path: string) {
    let rawRes = await fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      method: "GET",
    });

    return await _handleResponse<T>(rawRes);
  }

  async function getFile<T>(path: string) {
    let rawRes = await fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      method: "GET",
    });

    return rawRes.blob();
  }

  async function postGetFile<T>(path: string, body: T) {
    let rawRes = await fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      body: JSON.stringify(body),
      method: "POST",
    });

    return rawRes.blob();
  }

  async function put<TResponse = any, TBody = unknown>(path: string, body: TBody) {
    let rawRes = await fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      body: JSON.stringify(body),
      method: "PUT",
    });

    let res = await _handleResponse<TResponse>(rawRes);

    return res;
  }

  async function patch<TResponse = any, TBody = unknown>(path: string, body: TBody) {
    let rawRes = await fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      body: JSON.stringify(body),
      method: "PATCH",
    });

    let res = await _handleResponse<TResponse>(rawRes);

    return res;
  }

  async function post<TResponse = any, TBody = unknown>(path: string, body: TBody) {
    let rawRes = await fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      body: JSON.stringify(body),
      method: "POST",
    });

    let res = await _handleResponse<TResponse>(rawRes);

    return res;
  }

  async function postDocs<T>(path: string, body: any) {
    let rawRes = await fetch(`${baseUrl}${path}`, {
      body: body,
      headers: {
        ...(await _getBearer()),
      },
      ...config.options,
      method: "POST",
    });

    let res = await _handleResponse<T>(rawRes);

    return res;
  }

  async function del<T>(path: string, body?: T) {
    let rawRes = await fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      body: JSON.stringify(body),
      method: "DELETE",
    });

    let res = await _handleResponse<T>(rawRes);

    return res;
  }

  async function customerPostFormData<T>(path: string, body: any) {
    let rawRes = await fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: { "x-emailId": body.emailId },
      body: body.data,
    });

    let res = await _handleResponse<T>(rawRes);

    return res;
  }

  async function customerPatch<T>(path: string, body: any) {
    let rawRes = await fetch(`${baseUrl}${path}`, {
      headers: {
        "Content-Type": "application/json",
        "x-emailId": body.emailId,
      },
      body: JSON.stringify(body),
      method: "PATCH",
    });

    let res = await _handleResponse<T>(rawRes);

    return res;
  }

  return {
    get,
    getFile,
    put,
    patch,
    post,
    postDocs,
    postGetFile,
    customerPostFormData,
    customerPatch,
    del,
  };
}
